import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  Shield, FileText, Settings, LogOut, BarChart3, Globe, Tag,
  Users, Eye, Clock, MapPin, Smartphone, Monitor, Tablet,
  TrendingUp, TrendingDown, Calendar, Download, RefreshCw,
  Globe2, Search, ArrowUpRight, MousePointer
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnalytics = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');
  const [analytics, setAnalytics] = useState({
    summary: { total_visits: 0, unique_visitors: 0, page_views: 0, avg_time: 0 },
    visits_by_day: [],
    top_pages: [],
    traffic_sources: [],
    devices: { desktop: 0, mobile: 0, tablet: 0 },
    countries: [],
    cities: [],
    browsers: [],
    recent_visits: []
  });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/analytics/dashboard`, {
        params: { range: dateRange },
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Error al cargar analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/export`, {
        params: { range: dateRange },
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics_${dateRange}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Analytics exportados');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const getDeviceIcon = (device) => {
    switch (device?.toLowerCase()) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  const getSourceIcon = (source) => {
    const s = source?.toLowerCase() || '';
    if (s.includes('google')) return '🔍';
    if (s.includes('bing')) return '🔎';
    if (s.includes('facebook')) return '📘';
    if (s.includes('instagram')) return '📷';
    if (s.includes('twitter') || s.includes('x.com')) return '🐦';
    if (s.includes('youtube')) return '📺';
    if (s.includes('linkedin')) return '💼';
    if (s === 'direct') return '🔗';
    return '🌐';
  };

  const totalDevices = (analytics.devices?.desktop || 0) + (analytics.devices?.mobile || 0) + (analytics.devices?.tablet || 0);
  const devicePercentage = (count) => totalDevices > 0 ? Math.round((count / totalDevices) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="admin-analytics">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-screen w-64 bg-[#0A0A0A] text-white flex flex-col">
        <div className="p-6">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0047FF] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">A</span>
            </div>
            <div>
              <span className="font-heading font-bold text-lg">Arreglatelo</span>
              <p className="text-xs text-[#A1A1AA]">Admin Panel</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-4">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <BarChart3 className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Dashboard</span>
          </Link>
          <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 mb-1">
            <Eye className="w-5 h-5" />
            <span className="font-medium">Analytics</span>
          </Link>
          <Link to="/admin/warranties" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Shield className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Garantías</span>
          </Link>
          <Link to="/admin/invoices" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <FileText className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Facturas</span>
          </Link>
          <Link to="/admin/seo" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Globe className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">SEO & Contenido</span>
          </Link>
          <Link to="/admin/marketing" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Tag className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Marketing</span>
          </Link>
          <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Settings className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Configuración</span>
          </Link>
        </nav>

        <div className="p-4 border-t border-white/10">
          <Button
            variant="ghost"
            className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-white/5"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-7xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">Analytics & Tracking</h1>
              <p className="text-[#52525B]">Análisis de tráfico y comportamiento de visitantes</p>
            </div>
            <div className="flex gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="border rounded-lg px-4 py-2 bg-white"
              >
                <option value="today">Hoy</option>
                <option value="7d">Últimos 7 días</option>
                <option value="30d">Últimos 30 días</option>
                <option value="90d">Últimos 90 días</option>
                <option value="365d">Último año</option>
              </select>
              <Button variant="outline" onClick={fetchAnalytics}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
              <Button variant="outline" onClick={exportCSV}>
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0047FF]"></div>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl border p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Visitas Totales</span>
                    <Eye className="w-5 h-5 text-blue-500" />
                  </div>
                  <p className="text-3xl font-bold">{formatNumber(analytics.summary?.total_visits)}</p>
                  <div className="flex items-center mt-2 text-sm">
                    <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                    <span className="text-green-500">+12%</span>
                    <span className="text-gray-400 ml-1">vs periodo anterior</span>
                  </div>
                </div>

                <div className="bg-white rounded-xl border p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Visitantes Únicos</span>
                    <Users className="w-5 h-5 text-purple-500" />
                  </div>
                  <p className="text-3xl font-bold">{formatNumber(analytics.summary?.unique_visitors)}</p>
                  <div className="flex items-center mt-2 text-sm">
                    <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                    <span className="text-green-500">+8%</span>
                    <span className="text-gray-400 ml-1">vs periodo anterior</span>
                  </div>
                </div>

                <div className="bg-white rounded-xl border p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Páginas Vistas</span>
                    <MousePointer className="w-5 h-5 text-orange-500" />
                  </div>
                  <p className="text-3xl font-bold">{formatNumber(analytics.summary?.page_views)}</p>
                  <div className="flex items-center mt-2 text-sm">
                    <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                    <span className="text-red-500">-3%</span>
                    <span className="text-gray-400 ml-1">vs periodo anterior</span>
                  </div>
                </div>

                <div className="bg-white rounded-xl border p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Tiempo Medio</span>
                    <Clock className="w-5 h-5 text-green-500" />
                  </div>
                  <p className="text-3xl font-bold">{analytics.summary?.avg_time || '0:00'}</p>
                  <div className="flex items-center mt-2 text-sm">
                    <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                    <span className="text-green-500">+15%</span>
                    <span className="text-gray-400 ml-1">engagement</span>
                  </div>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {/* Visits Chart */}
                <div className="col-span-2 bg-white rounded-xl border p-6">
                  <h3 className="font-semibold mb-4">Visitas por Día</h3>
                  <div className="h-64 flex items-end gap-1">
                    {(analytics.visits_by_day || []).slice(-14).map((day, idx) => (
                      <div key={idx} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                          style={{ height: `${Math.max(10, (day.count / Math.max(...analytics.visits_by_day.map(d => d.count), 1)) * 200)}px` }}
                          title={`${day.date}: ${day.count} visitas`}
                        />
                        <span className="text-xs text-gray-400 mt-1 rotate-45 origin-left">{day.date?.slice(5)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Traffic Sources */}
                <div className="bg-white rounded-xl border p-6">
                  <h3 className="font-semibold mb-4">Fuentes de Tráfico</h3>
                  <div className="space-y-3">
                    {(analytics.traffic_sources || []).slice(0, 6).map((source, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getSourceIcon(source.name)}</span>
                          <span className="text-sm">{source.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${source.percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{source.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Second Row */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {/* Devices */}
                <div className="bg-white rounded-xl border p-6">
                  <h3 className="font-semibold mb-4">Dispositivos</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Monitor className="w-5 h-5 text-blue-500" />
                        <span>Desktop</span>
                      </div>
                      <span className="font-medium">{devicePercentage(analytics.devices?.desktop)}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${devicePercentage(analytics.devices?.desktop)}%` }} />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Smartphone className="w-5 h-5 text-green-500" />
                        <span>Mobile</span>
                      </div>
                      <span className="font-medium">{devicePercentage(analytics.devices?.mobile)}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: `${devicePercentage(analytics.devices?.mobile)}%` }} />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Tablet className="w-5 h-5 text-purple-500" />
                        <span>Tablet</span>
                      </div>
                      <span className="font-medium">{devicePercentage(analytics.devices?.tablet)}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500 rounded-full" style={{ width: `${devicePercentage(analytics.devices?.tablet)}%` }} />
                    </div>
                  </div>
                </div>

                {/* Top Pages */}
                <div className="bg-white rounded-xl border p-6">
                  <h3 className="font-semibold mb-4">Páginas Más Visitadas</h3>
                  <div className="space-y-3">
                    {(analytics.top_pages || []).slice(0, 8).map((page, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="truncate flex-1 text-gray-600" title={page.path}>{page.path}</span>
                        <span className="font-medium ml-2">{formatNumber(page.views)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Countries */}
                <div className="bg-white rounded-xl border p-6">
                  <h3 className="font-semibold mb-4">Países</h3>
                  <div className="space-y-3">
                    {(analytics.countries || []).slice(0, 8).map((country, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{country.flag || '🌍'}</span>
                          <span>{country.name}</span>
                        </div>
                        <span className="font-medium">{country.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recent Visits Table */}
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="p-4 border-b">
                  <h3 className="font-semibold">Visitas Recientes</h3>
                </div>
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Página</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Fuente</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Ubicación</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Dispositivo</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Fecha/Hora</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(analytics.recent_visits || []).slice(0, 15).map((visit, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <ArrowUpRight className="w-4 h-4 text-gray-400" />
                            <span className="text-sm truncate max-w-xs" title={visit.path}>{visit.path}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm flex items-center gap-1">
                            {getSourceIcon(visit.source)}
                            {visit.source}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-gray-400" />
                            {visit.city}, {visit.country}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm flex items-center gap-1">
                            {getDeviceIcon(visit.device)}
                            {visit.browser}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {visit.timestamp}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminAnalytics;
